// src/pages/PlatformGuide.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HighLogo from "../../HighLogo";
import logo from "../../../assets/logo.png";
import { ChevronRight } from "lucide-react";
import LanguageSelector from "../../components/LanguageSelector";

type ContentBlock =
  | { type: "paragraph"; text: string }
  | { type: "bullets"; items: string[] }
  | { type: "steps"; items: string[] }
  | { type: "note"; text: string };

type PlatformSection = {
  title: string;
  body: ContentBlock[];
};

type SidebarTopic = {
  id: string;
  label: string;
  children: {
    id: string;
    label: string;
  }[];
};

const PlatformGuide: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [showLanguage, setShowLanguage] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("platformOverview");
  const [openTopic, setOpenTopic] = useState("platformOverview");

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

 const sidebarTopics: SidebarTopic[] = useMemo(
    () => [
      {
        id: "platformOverview",
        label: t("platformGuide.sidebar.platformOverview"),
        children: [
          {
            id: "platformOverview",
            label: t("platformGuide.overview.platformOverview"),
          },
        ],
      },
      {
        id: "feeds",
        label: t("platformGuide.sidebar.feeds"),
        children: [
          {
            id: "feeds",
            label: t("platformGuide.overview.feeds"),
          },
          {
            id: "gallery",
            label: t("platformGuide.overview.gallery"),
          },
          {
            id: "myFeed",
            label: t("platformGuide.overview.myFeed"),
          },
          {
            id: "myLikes",
            label: t("platformGuide.overview.myLikes"),
          },
          {
            id: "locationPosts",
            label: t("platformGuide.overview.locationPosts"),
          },
          {
            id: "musicPosts",
            label: t("platformGuide.overview.musicPosts"),
          },
        ],
      },
      {
        id: "search",
        label: t("platformGuide.sidebar.search"),
        children: [
          {
            id: "search",
            label: t("platformGuide.overview.search"),
          },
          {
          id: "hiddenHashtags",
          label: t("platformGuide.overview.hiddenHashtags")
          },
         
          {
            id: "changeLocation",
            label: t("platformGuide.overview.changeLocation"),
          },
          {
            id: "currentLocation",
            label: t("platformGuide.overview.currentLocation"),
          }, 
        ],
      },
      {
        id: "content",
        label: t("platformGuide.sidebar.content"),
        children: [
          {
            id: "content",
            label: t("platformGuide.overview.content"),
          },
          {
            id: "uploadPost",
            label: t("platformGuide.overview.uploadPost"),
          },
          {
            id: "musicInMedia",
            label: t("platformGuide.overview.musicInMedia"),
          },
          {
            id: "editPost",
            label: t("platformGuide.overview.editPost"),
          },
          {
            id: "deletePost",
            label: t("platformGuide.overview.deletePost"),
          },
          {
            id: "postVisibility",
            label: t("platformGuide.overview.postVisibility"),
          },
        ],
      },
      {
        id: "chat",
        label: t("platformGuide.sidebar.chat"),
        children: [
          {
            id: "chat",
            label: t("platformGuide.overview.chat"),
          },
          {
            id: "searchUsers",
            label: t("platformGuide.overview.searchUsers"),
          },
          {
            id: "writeMessages",
            label: t("platformGuide.overview.writeMessages"),
          },
          {
            id: "deleteChat",
            label: t("platformGuide.overview.deleteChat"),
          },
          {
            id: "hideChat",
            label: t("platformGuide.overview.hideChat"),
          },
          {
            id: "deleteMessage",
            label: t("platformGuide.overview.deleteMessage"),
          },
          {
            id: "replyMessage",
            label: t("platformGuide.overview.replyMessage"),
          },
          {
            id: "copyMessage",
            label: t("platformGuide.overview.copyMessage"),
          },
        ],
      },
      {
        id: "notification",
        label: t("platformGuide.sidebar.notification"),
        children: [
          {
            id: "notification",
            label: t("platformGuide.overview.notification"),
          },
          {
            id: "chatNotification",
            label: t("platformGuide.overview.chatNotification"),
          },
          {
            id: "likeNotification",
            label: t("platformGuide.overview.likeNotification"),
          },
          {
            id: "followNotification",
            label: t("platformGuide.overview.followNotification"),
          },
        ],
      },
      {
        id: "myProfile",
        label: t("platformGuide.sidebar.myProfile"),
        children: [
          {
            id: "myProfile",
            label: t("platformGuide.overview.myProfile"),
          },
          {
            id: "myPosts",
            label: t("platformGuide.overview.myPosts"),
          },
          {
            id: "myFollowers",
            label: t("platformGuide.overview.myFollowers"),
          },
          {
            id: "iFollow",
            label: t("platformGuide.overview.iFollow"),
          },
          {
            id: "visitProfiles",
            label: t("platformGuide.overview.visitProfiles"),
          },
        ],
      },
      {
        id: "safety",
        label: t("platformGuide.sidebar.safety"),
        children: [
          {
            id: "safety",
            label: t("platformGuide.overview.safety"),
          },
          {
            id: "block",
            label: t("platformGuide.overview.block"),
          },
          {
            id: "reportProfiles",
            label: t("platformGuide.overview.reportProfiles"),
          },
          {
            id: "reportPosts",
            label: t("platformGuide.overview.reportPosts"),
          },
        ],
      },
      {
        id: "troubleshooting",
        label: t("platformGuide.sidebar.troubleshooting"),
        children: [
          {
            id: "troubleshooting",
            label: t("platformGuide.overview.troubleshooting"),
          },
          {
            id: "loadingIssues",
            label: t("platformGuide.overview.loadingIssues"),
          },
          {
            id: "uploadIssues",
            label: t("platformGuide.overview.uploadIssues"),
          },
          {
            id: "notificationIssues",
            label: t("platformGuide.overview.notificationIssues"),
          },
          {
            id: "messagesFail",
            label: t("platformGuide.overview.messagesFail"),
          },
          {
            id: "contactPlatformSupport",
            label: t("platformGuide.overview.contactPlatformSupport"),
          },
        ],
      },
    ],
    [t]
  );

  useEffect(() => {
  const hash = location.hash.replace("#", "");
  if (!hash) return;

  const parent = sidebarTopics.find((topic) =>
    topic.children.some((child) => child.id === hash)
  );

  if (parent) {
    setOpenTopic(parent.id);
    setActiveSection(hash);
  }
}, [location.hash, sidebarTopics]);

  const filteredSidebarTopics = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sidebarTopics;

    return sidebarTopics
      .map((topic) => {
        const matchingChildren = topic.children.filter((child) =>
          `${topic.label} ${child.label}`.toLowerCase().includes(q)
        );

        return {
          ...topic,
          children: matchingChildren,
        };
      })
      .filter((topic) => topic.children.length > 0);
  }, [searchQuery, sidebarTopics]);

  const section = t(`platformGuide.sections.${activeSection}`, {
    returnObjects: true,
  }) as PlatformSection;

  const renderBlock = (block: ContentBlock, index: number) => {
    if (block.type === "paragraph") {
      return (
        <p key={index} style={paragraph}>
          {block.text}
        </p>
      );
    }

    if (block.type === "bullets") {
      return (
        <ul key={index} style={bulletList}>
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex} style={bulletItem}>
              <span style={dot} aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    }

    if (block.type === "steps") {
      return (
        <ol key={index} style={stepsList}>
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex} style={stepItem}>
              {item}
            </li>
          ))}
        </ol>
      );
    }

    if (block.type === "note") {
      return (
        <div key={index} style={noteBox}>
          {block.text}
        </div>
      );
    }

    return null;
  };

  return (
    <div style={shell}>
      <header style={header}>
        <div style={headerLeft} onClick={() => navigate("/gallery")}>
          <HighLogo src={logo} style={{ width: 170, display: "block" }} padding={0} />
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginRight: 10 }}>
  <button
    type="button"
    onClick={() => navigate(-1)}
    style={ghostBtn}
    aria-label={t("platformGuide.meta.back", "Back")}
    title={t("platformGuide.meta.back", "Back")}
  >
    ← {t("platformGuide.meta.back", "Back")}
  </button>

  <button
    type="button"
    onClick={() => setShowLanguage(true)}
    style={ghostBtn}
  >
    🌐 {t("home.footer.language", "Language")}
  </button>
</div>
      </header>

      <main style={main} className="pg-main">
        <aside style={sidebar} className="pg-sidebar">
          <div style={sidebarHeader}>
            <div style={sidebarEyebrow}>
              {t("platformGuide.meta.overviewTitle", "Platform Guide")}
            </div>

            <h2 style={sidebarTitle}>
              {t("platformGuide.meta.title", "Platform Guide")}
            </h2>

            <p style={sidebarText}>
              {t(
                "platformGuide.meta.subtitle",
                "Learn how to use the platform, manage content, interact with others, and stay safe."
              )}
            </p>

            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("platformGuide.meta.searchPlaceholder")}
              style={searchInput}
            />
          </div>

          <nav style={nav}>
            {filteredSidebarTopics.map((topic) => {
              const isSearching = searchQuery.trim().length > 0;
              const isOpen = isSearching || openTopic === topic.id;
              const isActiveGroup = isOpen;

              return (
                <div key={topic.id} style={navGroup}>
                  <button
                    type="button"
                     onClick={() => {
  if (isSearching) return;
  setOpenTopic(openTopic === topic.id ? "" : topic.id);
}}
                    style={{
                      ...navBtn,
                      ...(isActiveGroup ? navBtnActive : null),
                    }}
                  >
                    <span>{topic.label}</span>
                    <ChevronRight
                      size={16}
                      strokeWidth={2.2}
                      style={{
                        ...chevron,
                        transform: isOpen ? "rotate(270deg)" : "rotate(90deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </button>

                  {isOpen && (
                    <div style={subNav}>
                      {topic.children.map((child) => {
                        const active = activeSection === child.id;

                        return (
                          <button
                            key={child.id}
                            type="button"
                            onClick={() => setActiveSection(child.id)}
                            style={{
                              ...subBtn,
                              ...(active ? subBtnActive : null),
                            }}
                          >
                            {child.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <section style={contentCard}>
          <div style={contentInner}>
            <div style={contentHeader}>
              <div style={heroLabel}>
                {t("platformGuide.meta.overviewTitle", "Platform Guide")}
              </div>
              <h1 style={h1}>{section?.title}</h1>
            </div>

            <div style={contentBody}>
              {Array.isArray(section?.body)
                ? section.body.map((block, index) => renderBlock(block, index))
                : null}
            </div>
          </div>
        </section>
      </main>

      {showLanguage && (
  <div style={languageOverlay} onClick={() => setShowLanguage(false)}>
    <div
      onClick={(e) => e.stopPropagation()}
      style={languageModalWrap}
    >
      <LanguageSelector
        onClose={() => setShowLanguage(false)}
        offsetY={0}
      />
    </div>
  </div>
)}

      <style>{`
        button {
          font-family: inherit;
        }

        button:hover {
          opacity: 0.92;
        }

        button:focus-visible {
          outline: 2px solid rgba(163,230,53,0.65);
          outline-offset: 3px;
        }

        ol li::marker {
          color: #84cc16;
          font-weight: 800;
        }

        @media (max-width: 900px) {
          .pg-main {
            grid-template-columns: 1fr !important;
            padding: 14px !important;
          }

          .pg-sidebar {
            max-height: 34vh;
          }
        }
      `}</style>
    </div>
  );
};

export default PlatformGuide;

const shell: React.CSSProperties = {
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#f9fafb",
  fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const header: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 25px",
  background: "rgba(2, 6, 23, 0.9)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(148,163,184,0.2)",
};

const headerLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  marginLeft: "-28px",
  marginTop: "5px",
};

const ghostBtn: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid #4b5563",
  background: "transparent",
  color: "#e5e7eb",
  cursor: "pointer",
  fontSize: 13,
};

const main: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: "grid",
  gridTemplateColumns: "390px minmax(0, 1fr)",
  gap: 22,
  padding: "18px 2vw",
  overflow: "hidden",
  boxSizing: "border-box",
};

const sidebar: React.CSSProperties = {
  minHeight: 0,
  borderRadius: 24,
  border: "1px solid rgba(148,163,184,0.2)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.92), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.72)",
  padding: 16,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const sidebarHeader: React.CSSProperties = {
  padding: "8px 8px 14px",
  borderBottom: "1px solid rgba(148,163,184,0.14)",
};

const sidebarEyebrow: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1.4,
  color: "#84cc16",
  fontWeight: 900,
};

const sidebarTitle: React.CSSProperties = {
  margin: "8px 0 0",
  fontSize: 22,
  lineHeight: 1.1,
  color: "#e5e7eb",
  letterSpacing: -0.04,
};

const sidebarText: React.CSSProperties = {
  margin: "10px 0 0",
  fontSize: 13,
  lineHeight: 1.45,
  color: "#8d9298",
};

const nav: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: "14px 4px 4px",
  display: "grid",
  gap: 8,
  alignContent: "start",
};

const navBtn: React.CSSProperties = {
  width: "100%",
  border: "1px solid rgba(148,163,184,0.16)",
  background: "rgba(255,255,255,0.035)",
  color: "#cbd5e1",
  borderRadius: 16,
  padding: "13px 12px",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 900,
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};

const navBtnActive: React.CSSProperties = {
  border: "1px solid rgba(163,230,53,0.4)",
  background: "rgba(163,230,53,0.1)",
  color: "#e5e7eb",
};

const contentCard: React.CSSProperties = {
  minHeight: 0,
  borderRadius: 24,
  border: "1px solid rgba(148,163,184,0.2)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.9), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.8)",
  overflow: "hidden",
};

const contentInner: React.CSSProperties = {
  height: "100%",
  overflowY: "auto",
  padding: "28px 34px 60px",
  boxSizing: "border-box",
};

const contentHeader: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.16)",
  background:
    "linear-gradient(135deg, rgba(163,230,53,0.09), rgba(255,255,255,0.035) 45%, rgba(2,6,23,0.4))",
  padding: "26px 28px",
  marginBottom: 22,
};

const heroLabel: React.CSSProperties = {
  color: "#84cc16",
  fontSize: 12,
  fontWeight: 950,
  textTransform: "uppercase",
  letterSpacing: 1.4,
};

const h1: React.CSSProperties = {
  margin: "8px 0 0",
  fontSize: 34,
  lineHeight: 1.05,
  color: "#e5e7eb",
  letterSpacing: -0.05,
};

const contentBody: React.CSSProperties = {
  maxWidth: 1100,
  padding: "4px 8px",
};

const paragraph: React.CSSProperties = {
  margin: "0 0 18px",
  color: "#cbd5e1",
  fontSize: 16,
  lineHeight: 1.8,
};

const stepsList: React.CSSProperties = {
  margin: "0 0 24px",
  paddingLeft: "34px",
  color: "#cbd5e1",
  fontSize: 15,
  lineHeight: 1.8,
  listStyleType: "decimal",
  listStylePosition: "outside",
};

const stepItem: React.CSSProperties = {
  marginBottom: 12,
  paddingLeft: 6,
  display: "list-item",
};

const bulletList: React.CSSProperties = {
  margin: "0 0 22px",
  padding: 0,
  listStyle: "none",
  display: "grid",
  gap: 12,
};

const bulletItem: React.CSSProperties = {
  listStyle: "none",
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
  color: "#cbd5e1",
  fontSize: 15,
  lineHeight: 1.7,
  paddingLeft: 16,
};

const dot: React.CSSProperties = {
  width: 5,
  height: 5,
  minWidth: 5,
  minHeight: 5,
  borderRadius: "50%",
  background: "#84cc16",
  flexShrink: 0,
  marginTop: 10,
};

const noteBox: React.CSSProperties = {
  margin: "18px 0 22px",
  padding: 16,
  borderRadius: 16,
  border: "1px solid rgba(163,230,53,0.25)",
  background: "rgba(163,230,53,0.08)",
  color: "#d9f99d",
  fontSize: 15,
  lineHeight: 1.6,
};

const navGroup: React.CSSProperties = {
  display: "grid",
  gap: 7,
};

const chevron: React.CSSProperties = {
  color: "#94a3b8",
  flex: "0 0 auto",
};

const subNav: React.CSSProperties = {
  marginLeft: 10,
  paddingLeft: 12,
  borderLeft: "1px solid rgba(148,163,184,0.18)",
  display: "grid",
  gap: 6,
};

const subBtn: React.CSSProperties = {
  width: "100%",
  border: "none",
  background: "transparent",
  color: "#8d9298",
  borderRadius: 12,
  padding: "9px 10px",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 750,
  textAlign: "left",
};

const subBtnActive: React.CSSProperties = {
  background: "rgba(255,255,255,0.07)",
  color: "#e5e7eb",
};

const searchInput: React.CSSProperties = {
  width: "100%",
  marginTop: 14,
  padding: "11px 13px",
  borderRadius: 14,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(255,255,255,0.045)",
  color: "#e5e7eb",
  outline: "none",
  fontSize: 13,
  boxSizing: "border-box",
};

const languageOverlay: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999999,
  backgroundColor: "rgba(0,0,0,0.78)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const languageModalWrap: React.CSSProperties = {
  transform: "translateY(5px)",
};